import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output} from '@angular/core';
import {DetectMethodChanges} from '../../utils';
import {SessionService} from '../../authentication/session-service.service';
import {AccessControlService} from '../../access-control-service.class';
import {MavenTemplateService} from './maven-template.service';
import {MavenTemplateEntry, MavenTemplateSettings} from './maven-settings-template';
import {MavenSharedTemplateClient, MavenTemplateAssignment} from './maven-shared-template-client';

@Component({selector: 'ets-maven-template-selector', templateUrl: './maven-template-selector.component.html',
    styleUrls: ['./maven-template-selector.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush})
export class MavenTemplateSelectorComponent implements OnChanges, OnDestroy {
    @Input() underlying: string | null = null;
    @Input() disabled = false;
    @Input() capture: () => MavenTemplateSettings;
    @Input() different = false;
    @Input() saveProblems: string[] = [];
    @Output() selected = new EventEmitter<MavenTemplateEntry>();
    // Updating this baseline never restores Setup: Save must preserve run-only working values.
    @Output() baselineChange = new EventEmitter<MavenTemplateEntry | null>();
    @Output() busyChange = new EventEmitter<boolean>();
    entries: MavenTemplateEntry[] = [];
    current: MavenTemplateEntry | null = null;
    busy = false;
    error = '';
    editor: 'new' | 'edit' | null = null;
    name = '';
    shared = false;
    deleting = false;
    assigning = false;
    assignments: MavenTemplateAssignment[] = [];
    assignmentGroups: {key: string; items: MavenTemplateAssignment[]}[] = [];
    // Grouped dx-list returns selected groups, not a flat array of template rows.
    selectedAssignments: {key: string; items: MavenTemplateAssignment[]}[] = [];
    get selectedAssignment(): MavenTemplateAssignment | null {
        return this.selectedAssignments[0]?.items[0] || null;
    }
    loadingMessage = 'Loading Templates…';
    private _generation = 0;
    private _family: string | null = null;
    private _destroyed = false;
    private _dropdownSource: MavenTemplateEntry[];
    private _dropdownEntries: {key: string; label: string}[] = [];

    get dropdownEntries(): {key: string; label: string}[] {
        if (this.entries !== this._dropdownSource) {
            this._dropdownSource = this.entries;
            // DevExtreme treats an item's `template` property as a rendering template.
            // Keep domain payloads out of the dropdown's display objects.
            this._dropdownEntries = this.entries.map(entry => ({key: entry.key, label: entry.label}));
        }
        return this._dropdownEntries;
    }

    constructor(private readonly _changeDetector: ChangeDetectorRef,
        private readonly _templates: MavenTemplateService, private readonly _shared: MavenSharedTemplateClient,
        private readonly _session: SessionService, private readonly _access: AccessControlService) {}

    get canManage(): boolean { return this._session.isSuperUser; }
    get canShare(): boolean {
        return this.canManage && this._access.isSecureElementAvailable('baab84a2-4ea1-4503-8697-f9a62a5df1a6');
    }
    get writable(): boolean { return !!this.current && (this.current.scope === 'personal' || this.canManage); }
    get locked(): boolean { return this.disabled || this.busy || !this.underlying; }

    ngOnChanges(): void {
        if (this.underlying !== this._family) {
            this._family = this.underlying;
            this.reload(true);
        }
    }
    ngOnDestroy(): void { this._destroyed = true; this._generation++; }

    private setBusy(value: boolean): void { this.busy = value; this.busyChange.emit(value); }
    private repaint(): void { if (!this._destroyed) { this._changeDetector.detectChanges(); } }

    async reload(restore = false): Promise<void> {
        const generation = ++this._generation;
        const underlying = this.underlying;
        this.error = ''; this.entries = []; this.current = null; this.editor = null; this.assigning = false;
        this.baselineChange.emit(null);
        if (!underlying) { this.setBusy(false); this.repaint(); return; }
        this.loadingMessage = 'Loading Templates…'; this.setBusy(true); this.repaint();
        try {
            const entries = await this._templates.list(underlying);
            if (generation !== this._generation) { return; }
            this.entries = entries;
            if (restore) {
                this.current = this._templates.lastUsed(underlying, entries);
                if (this.current) { this.selected.emit(this.current); }
                this.baselineChange.emit(this.current);
            }
        } catch (e) { if (generation === this._generation) { this.error = 'Could Not Load Shared Templates. Retry to Restore Your Template.'; } }
        finally { if (generation === this._generation) { this.setBusy(false); this.repaint(); } }
    }

    async choose(event: any): Promise<void> {
        if (!event?.event || this.locked) { return; }
        await this.applySelection(event.value);
    }

    async reselect(key: string): Promise<void> {
        // Selecting the current dropdown row does not raise DevExtreme's valueChanged event.
        // It is still an explicit request to restore the saved settings after working edits.
        if (this.locked || this.current?.key !== key) { return; }
        await this.applySelection(key);
    }

    private async applySelection(key: string): Promise<void> {
        await this.perform(async () => {
            // Refresh before selection: a revoked shared assignment must not be loaded from cache.
            this.entries = await this._templates.list(this.underlying);
            const entry = this.entries.filter(e => e.key === key)[0];
            if (!entry) { throw new Error('Template No Longer Exists or Is No Longer Assigned.'); }
            this.current = entry;
            this.selected.emit(entry);
            this.baselineChange.emit(entry);
            await this._templates.remember(this.underlying, entry);
        }, 'Loading Template…');
    }
    @DetectMethodChanges()
    openEditor(mode: 'new' | 'edit'): void {
        if (this.locked || (mode === 'edit' && !this.writable)
            || (mode === 'new' && this.saveProblems.length)) { return; }
        this.editor = mode; this.error = '';
        this.name = mode === 'edit' ? this.current.template.name : '';
        this.shared = mode === 'edit' && this.current.scope === 'shared';
    }
    @DetectMethodChanges()
    closeEditor(): void { if (!this.busy) { this.editor = null; } }
    @DetectMethodChanges()
    onNameChanged(event: any): void { this.name = event.value || ''; }
    @DetectMethodChanges()
    onSharedChanged(event: any): void { this.shared = !!event.value; }
    @DetectMethodChanges()
    requestDelete(): void { this.deleting = true; }
    @DetectMethodChanges()
    cancelDelete(): void { this.deleting = false; }

    async save(fromEditor = false): Promise<void> {
        if (this.locked || (fromEditor && !this.editor)) { return; }
        const metadataOnly = fromEditor && this.editor === 'edit';
        if (!metadataOnly && this.saveProblems.length) {
            this.error = this.saveProblems.join(' '); this.repaint(); return;
        }
        const previous = fromEditor && this.editor === 'new' ? undefined : this.current;
        if ((!fromEditor || metadataOnly) && !this.writable) { return; }
        const scope = fromEditor ? (this.shared ? 'shared' : 'personal') : previous.scope;
        const name = fromEditor ? this.name : previous.template.name;
        if (scope === 'shared' && !this.canShare) { this.error = 'Shared Template Permission Is Required.'; this.repaint(); return; }
        await this.perform(async () => {
            // Edit changes metadata only; Save captures the current working setup.
            const entry = metadataOnly
                ? await this._templates.editMetadata(this.underlying, name, scope, previous)
                : await this._templates.save(this.underlying, name, this.capture(), scope, previous);
            this.current = entry;
            this.baselineChange.emit(entry);
            this.editor = null;
            this.entries = await this._templates.list(this.underlying);
            await this._templates.remember(this.underlying, entry);
        }, 'Saving Template…');
    }
    async remove(): Promise<void> {
        if (this.locked || !this.writable) { return; }
        await this.perform(async () => {
            await this._templates.remove(this.current);
            this.current = null; this.deleting = false;
            this.baselineChange.emit(null);
            this.entries = await this._templates.list(this.underlying);
        }, 'Deleting Template…');
    }
    async openAssignments(): Promise<void> {
        if (this.locked || !this.canManage) { return; }
        this.assignments = []; this.assignmentGroups = []; this.selectedAssignments = [];
        this.assigning = true;
        await this.perform(async () => {
            this.assignments = await this._shared.assignments();
            const families = Array.from(new Set(this.assignments.map(t => t.underlying))).sort();
            this.assignmentGroups = families.map(key => ({key, items: this.assignments
                .filter(t => t.underlying === key).sort((a, b) => a.name.localeCompare(b.name))}));
        }, 'Loading Templates and Assignments…');
    }
    @DetectMethodChanges()
    closeAssignments(): void {
        if (!this.busy) { this.assigning = false; this.assignments = []; this.assignmentGroups = []; this.selectedAssignments = []; }
    }
    @DetectMethodChanges()
    assignmentSelectionChanged(): void {}
    async saveAssignments(): Promise<void> {
        if (this.busy || !this.canManage || !this.assignments.length) { return; }
        await this.perform(async () => { await this._shared.assign(this.assignments); this.assigning = false; },
            'Saving Assignments…');
    }
    private async perform(work: () => Promise<void>, message = 'Loading Templates…'): Promise<void> {
        this.error = ''; this.loadingMessage = message; this.setBusy(true); this.repaint();
        try { await work(); }
        catch (e) { this.error = e?.message || 'Template Operation Failed. Please Retry.'; }
        finally { this.setBusy(false); this.repaint(); }
    }
}
